/* eslint-disable @typescript-eslint/no-explicit-any */
import { Switch } from "@headlessui/react";

import { Dispatch, SetStateAction } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button";
import { ProfileTabs } from "@/components/gate/profiles/types";
import ChooseGender from "@/components/helpers/ChooseGender";
import { IconEmptyUser, IconPencil } from "@/components/icons";
import DateOfBirthInput from "@/components/inputs/DateOfBirthInput";
import Input from "@/components/inputs/Input";
import UpdateProfileLayout from "@/components/layout/UpdateProfileLayout";

import "@/styles/datepicker.css";

import { ProfileForm } from "./ProfilesPage";

const CreateProfilePage = ({ setTab }: { setTab: Dispatch<SetStateAction<ProfileTabs>> }) => {
  const { t } = useTranslation();
  const {
    setError,
    getValues,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ProfileForm>();
  const picture = getValues("picture");
  const isSetPin = watch("pin_code");

  const handleNext = () => {
    const gender = getValues("gender");
    const date_of_birth = getValues("date_of_birth");
    const name = getValues("name");

    if (!gender) {
      setError("gender", { message: t("error.need-gender") });
      return;
    } else if (!date_of_birth) {
      setError("date_of_birth", { type: "manual", message: t("error.need-date-of-birth") });
      return;
    } else if (name.length < 1) {
      setError("name", { type: "manual", message: t("error.required") });
      return;
    } else {
      setTab(ProfileTabs.PROFILE_LANG);
    }
  };

  const handleSetPin = () => {
    if (!isSetPin) {
      setTab(ProfileTabs.PIN);
    } else {
      setValue("pin_code", "");
    }
  };

  const pushBack = (): void => {
    setTab(ProfileTabs.PROFILES);
  };

  return (
    <UpdateProfileLayout onBackClick={pushBack} title={t("Actions.add-profile")}>
      <hr className="mb-8 mt-4 h-px w-full max-w-[640px] border-surface-container max-md:max-w-full" />
      <div className="w-full max-w-[640px] items-center max-md:h-full max-md:max-w-full">
        <div className="mt-8 flex w-full justify-between gap-10 max-md:flex-col max-md:items-center">
          <div className="relative h-fit">
            <div
              className="size-40 min-w-40 self-center overflow-hidden rounded-full"
              style={{
                background: picture?.color || "linear-gradient(rgba(255, 75, 0, 0.6) 0%, rgba(255, 75, 0, 0.25) 100%)",
              }}
            >
              {picture?.resized ? (
                <img
                  src={picture?.resized}
                  alt={"profile-image"}
                  className={"size-40 translate-y-[1.5rem] scale-110 transform object-contain max-sm:size-[6.25rem]"}
                />
              ) : (
                <IconEmptyUser className="size-40 w-40" />
              )}
            </div>
            <button
              type={"button"}
              title={t("Actions.edit")}
              onClick={() => setTab(ProfileTabs.IMAGE)}
              className={"absolute bottom-0 right-3 rounded-full bg-surface-container p-2 max-sm:right-0"}
            >
              <IconPencil variant={1} className={"size-4 stroke-on-primary"} />
            </button>
          </div>
          <form className="w-full space-y-4">
            <Input
              type={"text"}
              placeholder={t("Form.nickname")}
              className="w-full text-on-primary"
              errorMessage={errors.name?.message}
              register={register}
              required
              name="name"
            />
            <div className="w-full">
              <ChooseGender />
            </div>
            <div className="grid-cols-1 pb-2">
              <p className="mb-2">{t("Form.date-of-birth")}</p>
              <DateOfBirthInput />
            </div>

            <div className="flex w-full justify-between gap-3 rounded-2xl bg-surface-bright p-4">
              <div>
                <h4>{t("Profile.pin-code-set")}</h4>
                <p className="mt-1 max-w-[271px] text-[14px] text-[#919090]">{t("Profile.set-pin-desc")}</p>
              </div>
              <div onClick={handleSetPin}>
                <Switch
                  checked={!!isSetPin}
                  className="group relative flex h-6 w-10 cursor-pointer rounded-full bg-grey-1 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[checked]:bg-orange-1 data-[focus]:outline-1 data-[focus]:outline-white"
                >
                  <span
                    aria-hidden="true"
                    className="group-data-[checked]: pointer-events-none inline-block size-4 translate-x-0 rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-4"
                  />
                </Switch>
              </div>
            </div>
            <div className="flex gap-6 pt-4">
              <Button
                color="black"
                className="max-md:hidden"
                size="medium"
                onClick={() => setTab(ProfileTabs.CONTENT_LANG)}
              >
                {t("Actions.cancel")}
              </Button>
              <Button type="button" size="medium" onClick={handleNext}>
                {t("Actions.continue")}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <div className="h-20"></div>
    </UpdateProfileLayout>
  );
};

export default CreateProfilePage;
